import { Component, Inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Observable, Subject, catchError, distinctUntilChanged, finalize, of, switchMap, takeUntil, tap } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { helper } from 'src/environments/helper';
import { CereriService } from '../cereri.service';
import { UsersService } from 'src/app/services/users.service';
import { NotificationService } from 'src/app/services/notification.service';
import { UpdateMassAssignPayload } from '../cereri.interface';
import { CommonService } from 'src/app/services/common.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-cereri-asigneaza',
    templateUrl: './cereri-asigneaza.component.html',
    styleUrls: ['./cereri-asigneaza.component.scss'],
})
export class CereriAsigneazaComponent {
    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    helper = helper;

    isLoading = false;

    dgaspc_id = environment.interop.dgaspcId;

    private destroy$ = new Subject<void>();

    directiiActive: Generic[] = [];
    filteredDirectii: Generic[] = [];
    directiiControl = new FormControl();

    compartimenteActive: Generic[] = [];
    filteredCompartimente: Generic[] = [];
    compartimenteControl = new FormControl();

    usersActive: Generic[] = [];
    filteredUsers: Generic[] = [];
    usersControl = new FormControl();

    form: FormGroup = new FormGroup({
        directie_id: new FormControl(this.dgaspc_id, [Validators.required]),
        compartiment_id: new FormControl(''),
        operator: new FormControl(''),
    });

    constructor(
        @Inject(MAT_DIALOG_DATA)
        public data: number[],
        private dialogRef: MatDialogRef<CereriAsigneazaComponent>,
        private commonService: CommonService,
        private cereriService: CereriService,
        private usersService: UsersService,
        private notificationService: NotificationService
    ) { }

    ngOnInit(): void {
        this.loadDirectiiActive();
        this.loadCompartimenteActive(this.dgaspc_id);
        this.loadUsersActive();

        this.renderFieldsSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    closeDialog() {
        this.dialogRef?.close({ success: false });
    }

    renderFieldsSearch(): void {
        this.directiiControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredDirectii = this.directiiActive;
                    return of(this.directiiActive);
                }
                return this.filterDirectii(inputValue);
            }),
            tap((data: Generic[]) => { this.filteredDirectii = data; }),
            catchError(() => of([]))
        ).subscribe();

        this.compartimenteControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredCompartimente = this.compartimenteActive;
                    return of(this.compartimenteActive);
                }
                return this.filterCompartimente(inputValue);
            }),
            tap((data: Generic[]) => { this.filteredCompartimente = data; }),
            catchError(() => of([]))
        ).subscribe();

        this.usersControl.valueChanges.pipe(
            takeUntil(this.destroy$),
            distinctUntilChanged(),
            switchMap((inputValue) => {
                if (!inputValue || inputValue.length < 1) {
                    this.filteredUsers = this.usersActive;
                    return of(this.usersActive);
                }
                return this.filterOperatori(inputValue);
            }),
            tap((data: Generic[]) => { this.filteredUsers = data; }),
            catchError(() => of([]))
        ).subscribe();
    }

    loadDirectiiActive(): void {
        this.isLoading = true;
        this.commonService.getDirectiiActive()
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (results) => {
                    this.directiiActive = results;
                    this.filteredDirectii = [...results];
                },
                error: (err) => {
                    console.error('Error loading directii', err);
                    this.filteredDirectii = [];
                },
            });
    }

    onDirectieClosed(): void {
        const isSelectedInFilteredList = this.filteredDirectii.some(
            (d) => d.id === this.form.get('directie_id')?.value
        );
        if (!isSelectedInFilteredList) {
            this.directiiControl.setValue(null);
            this.filteredDirectii = this.directiiActive;
            this.destroy$.next();
            this.destroy$.complete();
            this.renderFieldsSearch();
        }
    }

    onDirectieChange(directieId: number): void {
        this.form.controls['compartiment_id'].reset();
        this.form.controls['operator'].reset();

        if (directieId > 0) {
            this.loadCompartimenteActive(directieId);
            this.loadUsersActive();
        } else {
            this.compartimenteActive = [];
            this.filteredCompartimente = [];
            this.usersActive = [];
            this.filteredUsers = [];
        }
    }

    loadCompartimenteActive(directieId?: number): void {
        this.isLoading = true;
        const compartimente$ = directieId
            ? this.cereriService.getCompartimenteByDirectieId(directieId)
            : this.cereriService.getCompartimenteActive();

        compartimente$
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (data) => {
                    this.compartimenteActive = data;
                    this.filteredCompartimente = [...data];
                },
                error: (err) => {
                    console.error('Error loading compartimente', err);
                    this.filteredCompartimente = [];
                },
            });
    }

    onCompartimentClosed(): void {
        const isSelectedInFilteredList = this.filteredCompartimente.some(
            (c) => c.id === this.form.get('compartiment_id')?.value
        );
        if (!isSelectedInFilteredList) {
            this.compartimenteControl.setValue(null);
            this.filteredCompartimente = this.compartimenteActive;
            this.destroy$.next();
            this.destroy$.complete();
            this.renderFieldsSearch();
        }
    }

    onCompartimentChange(compartimentId: number): void {
        this.form.controls['operator'].reset();

        if (compartimentId > 0) {
            this.loadUsersActive();
        } else {
            this.usersActive = [];
            this.filteredUsers = [];
        }
    }

    loadUsersActive(): void {
        this.isLoading = true;
        this.usersService.getUsers()
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (data) => {
                    this.usersActive = data;
                    this.filteredUsers = [...data];
                },
                error: (err) => {
                    console.error('Error loading users', err);
                    this.filteredUsers = [];
                },
            });
    }

    onUsersClosed(): void {
        const isSelectedInFilteredList = this.filteredUsers.some(
            (u) => u.id === this.form.get('operator')?.value
        );
        if (!isSelectedInFilteredList) {
            this.usersControl.setValue(null);
            this.filteredUsers = this.usersActive;
            this.destroy$.next();
            this.destroy$.complete();
            this.renderFieldsSearch();
        }
    }

    normalizeString(value: string): string {
        return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    }

    filterDirectii(searchTerm: string): Observable<Generic[]> {
        const normalized = this.normalizeString(searchTerm);
        return of(this.directiiActive.filter((d) =>
            this.normalizeString(d.name).includes(normalized)
        ));
    }

    filterCompartimente(searchTerm: string): Observable<Generic[]> {
        const normalized = this.normalizeString(searchTerm);
        return of(this.compartimenteActive.filter((c) =>
            this.normalizeString(c.name).includes(normalized)
        ));
    }

    filterOperatori(searchTerm: string): Observable<Generic[]> {
        const normalized = this.normalizeString(searchTerm);
        return of(this.usersActive.filter((u) =>
            this.normalizeString(u.name).includes(normalized)
        ));
    }

    submitForm() {
        this.isLoading = true;

        const ids_cerere = this.data;
        const directie_id = this.form.get('directie_id')?.value;
        const compartiment_id = this.form.get('compartiment_id')?.value;
        const user_id = this.form.get('operator')?.value;

        const newAssign: UpdateMassAssignPayload = {
            ids_cerere,
            directie_id: directie_id,
            compartiment_id: compartiment_id,
            user_id: user_id,
        };

        this.cereriService
            .updateMassAssign(newAssign)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.dialogRef.close({ success: true });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
